<template>
  <v-container>
    <v-row
      justify="center"
      v-if="!loadingWithText && !loading && !errorMsg"
      class="mb-1 pb-0"
    >
      <v-col cols="12" md="10" lg="8" class="text-center">
        <h1 class="text-h4 font-weight-bold mb-2">AI Resume review result</h1>
        <p class="text-subtitle-1">
          Analysis of your resume's effectiveness and potential improvements.
        </p>
      </v-col>
    </v-row>

    <!-- Text loader -->
    <div
      class="d-flex justify-center align-center"
      style="height: 70vh"
      v-if="loadingWithText && !loading && !errorMsg"
    >
      <v-row>
        <v-col cols="12" class="mb-0">
          <TextSwitchingLoader />
        </v-col>
        <v-col cols="12" class="text-center mt-0">
          <p class="text-subtitle-1" v-if="takingLongerThanExpected">
            Sorry it's taking so long, just hang tight!
          </p>
        </v-col>
      </v-row>
    </div>

    <!-- Skeleton loader -->
    <v-row v-if="loading && !errorMsg && !loadingWithText" justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-skeleton-loader type="card-heading"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="10" lg="8" class="mt-3">
        <v-skeleton-loader type="card-heading, image"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="10" lg="8" class="mt-3">
        <v-skeleton-loader
          type="article, list-item-three-line"
        ></v-skeleton-loader>
      </v-col>
    </v-row>

    <v-row v-if="errorMsg" justify="center" class="mt-5 mb-5">
      <v-col cols="12" md="10" lg="8">
        <v-alert type="error" outlined>{{ errorMsg }}</v-alert>
      </v-col>
    </v-row>

    <v-row justify="center" v-if="!loadingWithText && !loading && !errorMsg">
      <v-col cols="12" md="10" lg="8" class="mt-0 mb-0 pt-0 pb-0">
        <v-card class="mb-6">
          <v-card-title class="d-flex align-center">
            <p class="text-h6 text--secondary mt-0 mb-0 ml-1">
              {{ review.ResumeFileName }}
            </p>
            <v-spacer></v-spacer>
            <v-btn
              outlined
              text
              :to="`/displayresume?reviewId=${reviewId}`"
              :disabled="reviewId.endsWith('-example')"
            >
              open file
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>

      <!-- overall stuff (applies to all review types) -->
      <v-col cols="12" md="10" lg="8" class="mt-0 mb-0 pt-0 pb-0">
        <v-card class="mb-6">
          <v-card-title class="text-h5">
            Overall Score <v-spacer></v-spacer>
            <v-chip
              small
              :color="this.$consts.TIER_MAP.get(review.Tier).color"
              text-color="white"
            >
              {{ this.$consts.TIER_MAP.get(review.Tier).name, }}
            </v-chip>
          </v-card-title>
          <v-card-text class="text-center">
            <v-progress-circular
              :rotate="-90"
              :size="150"
              :width="15"
              :value="overallScoreCircle"
              :color="$colorUtils.overallScoreColor(overallScoreCircle)"
              class="my-4 mb-5"
            >
              <span class="text-h4 font-weight-bold">
                {{ overallScoreTxt }}%
              </span>
            </v-progress-circular>
            <p class="text-body-1 mx-auto">
              {{ review.OverallNote }}
            </p>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- basic and full review stuff -->
      <v-col
        cols="12"
        md="10"
        lg="8"
        class="mt-0 mb-0 pt-0 pb-0"
        v-if="!targetedTierReview()"
      >
        <v-card
          class="mb-6"
          v-if="(!isProUser && basicTierReview()) || !basicTierReview()"
        >
          <v-card-title class="text-h5">
            <div>Career progression</div>
            <v-icon color="grey" class="ml-2" v-if="basicTierReview()">
              mdi-lock-outline
            </v-icon>
          </v-card-title>
          <v-card-text>
            <v-row v-if="!basicTierReview()">
              <v-col cols="12">
                <v-slider
                  v-model="review.CareerProgressionScore"
                  :tick-labels="careerProgressionTicksLabels"
                  :max="careerProgressionTicksLabels.length - 1"
                  track-color="green darken-2"
                  color="green darken-2"
                  step="1"
                  ticks="always"
                  tick-size="0"
                  readonly
                ></v-slider>
              </v-col>
              <v-col>
                <p class="text-body-1">
                  {{ review.CareerProgressionNote }}
                </p>
              </v-col>
            </v-row>
            <v-row v-if="!isProUser && basicTierReview()">
              <v-col cols="12">
                Check available in the Full AI tier
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock with PRO
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="mb-6"
          v-if="(!isProUser && basicTierReview()) || !basicTierReview()"
        >
          <v-card-title class="text-h5">
            Technologies
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="numberFromString('3')"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
            <v-icon color="grey" class="ml-2" v-if="basicTierReview()">
              mdi-lock-outline
            </v-icon>
          </v-card-title>
          <v-card-text>
            <v-row v-if="!basicTierReview()">
              <v-col v-if="!review.Technologies">
                <p class="text-body-1">No technologies detected</p>
              </v-col>
              <v-col
                cols="12"
                class="mt-2 mb-2 text-h6"
                v-for="tech in review.Technologies"
                :key="tech.name"
              >
                <p>{{ tech.name }}</p>
                <v-row v-for="(it, itemIndex) in tech.items" :key="it.id">
                  <v-col
                    cols="12"
                    class="d-flex justify-space-between mt-2 ml-2"
                  >
                    <TechnologyIcon :id="it.id" :caption="it.name" />
                    <div class="ml-4 mr-4">
                      <p class="text-body-1">
                        {{ it.description }}
                      </p>
                    </div>
                    <ExperienceLevel class="mr-2" :level="it.level" />
                  </v-col>
                  <v-col
                    cols="12"
                    class="mt-0 mb-0"
                    v-if="itemIndex < tech.items.length - 1"
                  >
                    <v-divider></v-divider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-row v-if="!isProUser && basicTierReview()">
              <v-col cols="12">
                Check available in the Full AI tier
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock with PRO
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6">
          <v-card-title class="text-h5">
            <div>Parsability</div>
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="review.Parsability.impact"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
          </v-card-title>
          <v-card-subtitle class="text-caption">
            {{ review.Parsability.description }}
          </v-card-subtitle>
          <v-card-text>
            <v-row>
              <v-col cols="12" class="d-flex justify-space-between">
                <div class="text-body-1">
                  {{ review.Parsability.note }}
                </div>
                <div>
                  <p class="green--text text--darken-1 text-h5 ml-2">
                    <strong class="green--text text--darken-1 text-h3">{{
                      review.Parsability.score
                    }}</strong
                    >/10
                  </p>
                </div>
              </v-col>
              <v-col cols="12" class="subtitle-2 mb-0 pb-0 mt-1 pt-0">
                Actions
              </v-col>
              <v-col cols="12" class="text-body-1">
                <ul>
                  <li
                    v-for="action in review.Parsability.actions"
                    :key="action"
                  >
                    {{ action }}
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6">
          <v-card-title class="text-h5"> Data checks </v-card-title>
          <v-card-text>
            <v-row>
              <v-col
                cols="12"
                sm="6"
                v-for="dataCheck in review.DataChecks"
                :key="dataCheck.name"
              >
                <div class="d-flex justify-space-between text-body-1">
                  {{ dataCheck.name }}
                  <v-icon :color="dataCheck.value ? 'green' : 'red'">
                    {{ dataCheck.value ? "mdi-check-bold" : "mdi-close-thick" }}
                  </v-icon>
                </div>
              </v-col>
            </v-row>
            <v-row class="fade-out" v-if="!isProUser && basicTierReview()">
              <v-col
                cols="12"
                sm="6"
                v-for="n in 4"
                v-bind:key="n"
                class="pa-2"
              >
                <div
                  class="d-flex justify-center text-body-1"
                  style="
                    background-color: #e8e8e8;
                    padding: 10px;
                    border-radius: 4px;
                    border: 1px solid #ccc;
                  "
                >
                  <v-icon color="grey"> mdi-lock-outline </v-icon>
                </div>
              </v-col>
            </v-row>
            <v-row v-if="!isProUser && basicTierReview()">
              <v-col cols="12">
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock more with PRO
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="mb-6"
          v-if="(!isProUser && basicTierReview()) || !basicTierReview()"
        >
          <v-card-title class="text-h5">
            Data quality
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="numberFromString('2')"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
            <v-icon color="grey" class="ml-2" v-if="basicTierReview()">
              mdi-lock-outline
            </v-icon>
          </v-card-title>
          <v-card-subtitle class="text-caption">
            Checks the quality of data presented in your resume.
          </v-card-subtitle>
          <v-card-text v-if="basicTierReview()">
            <v-row>
              <v-col cols="12">
                Check available in the Full AI tier
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock with PRO
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-text v-if="!basicTierReview()">
            <v-row>
              <v-col cols="12" class="d-flex flex-row text-h6"
                >{{ review.DataQuality.name }}
                <div class="ml-2">
                  <v-rating
                    length="3"
                    dense
                    size="18"
                    readonly
                    :value="review.DataQuality.impact"
                    empty-icon="mdi-lightning-bolt-outline"
                    full-icon="mdi-lightning-bolt"
                    background-color="grey"
                    :color="impactColor(review.DataQuality.impact)"
                  >
                  </v-rating>
                </div>
              </v-col>
              <v-col cols="12" class="d-flex justify-space-between mt-0 pt-0">
                <div>{{ review.DataQuality.note }}</div>
                <div
                  class="text-h7 ml-2"
                  :class="scoreColorText(review.DataQuality.score)"
                >
                  <strong class="text-h4">{{ review.DataQuality.score }}</strong
                  >/10
                </div>
              </v-col>
              <v-col cols="12" class="subtitle-2 mb-0 pb-0 mt-1 pt-0">
                Actions
              </v-col>
              <v-col cols="12" class="text-body-1">
                <ul>
                  <li
                    v-for="action in review.DataQuality.actions"
                    :key="action"
                  >
                    {{ action }}
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6">
          <v-card-title class="text-h5"> Format </v-card-title>
          <v-card-text>
            <v-row v-for="(item, index) in review.Format" :key="index">
              <v-col cols="12" class="d-flex flex-row text-h6"
                >{{ item.name }}
                <div class="ml-2">
                  <v-rating
                    length="3"
                    dense
                    size="18"
                    readonly
                    :value="item.impact"
                    empty-icon="mdi-lightning-bolt-outline"
                    full-icon="mdi-lightning-bolt"
                    background-color="grey"
                    :color="impactColor(item.impact)"
                  >
                  </v-rating>
                </div>
              </v-col>
              <v-col cols="12" class="d-flex justify-space-between mt-0 pt-0">
                <div>{{ item.note }}</div>
                <div class="text-h7 ml-2" :class="scoreColorText(item.score)">
                  <strong class="text-h4">{{ item.score }}</strong
                  >/10
                </div>
              </v-col>
              <v-col
                cols="12"
                class="subtitle-2 mb-0 pb-0 mt-1 pt-0"
                v-if="item.actions?.length"
              >
                Actions
              </v-col>
              <v-col cols="12" class="text-body-1">
                <ul>
                  <li v-for="action in item.actions" :key="action">
                    {{ action }}
                  </li>
                </ul>
              </v-col>
              <!-- add divider between items except for the last one, which is "Format Consistency" -->
              <v-col class="mt-1 pt-0" v-if="item.name != 'Format Consistency'"
                ><v-divider></v-divider
              ></v-col>
            </v-row>
            <v-row class="fade-out" v-if="!isProUser && basicTierReview()">
              <v-col cols="12" class="d-flex flex-row text-h6">
                Wording
                <div class="ml-2">
                  <v-rating
                    length="3"
                    dense
                    size="18"
                    readonly
                    :value="numberFromString('3')"
                    empty-icon="mdi-lightning-bolt-outline"
                    full-icon="mdi-lightning-bolt"
                    background-color="grey"
                    :color="impactColor(3)"
                  >
                  </v-rating>
                </div>
              </v-col>
            </v-row>
            <v-row v-if="!isProUser && basicTierReview()">
              <v-col cols="12" class="mt-0 pt-0">
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock more with PRO
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="mb-6"
          v-if="(!isProUser && basicTierReview()) || !basicTierReview()"
        >
          <v-card-title class="text-h5">
            Job hopping potential
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="numberFromString('2')"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
            <v-icon color="grey" class="ml-2" v-if="basicTierReview()">
              mdi-lock-outline
            </v-icon>
          </v-card-title>
          <v-card-subtitle class="text-caption">
            Assesses consistency in your employment history, promotions, and
            explains any gaps or job-hopping.
          </v-card-subtitle>
          <v-card-text>
            <v-row v-if="basicTierReview()">
              <v-col cols="12">
                Check available in the Full AI tier
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock with PRO
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="!basicTierReview()">
              <v-col cols="12" class="d-flex justify-space-between">
                <div class="text-body-1">
                  {{ review.JobHopping.note }}
                </div>
                <div>
                  <p class="green--text text--darken-1 text-h5 ml-2">
                    <strong class="green--text text--darken-1 text-h3">{{
                      review.JobHopping.score
                    }}</strong
                    >/10
                  </p>
                </div>
              </v-col>
              <v-col
                cols="12"
                class="subtitle-2 mb-0 pb-0 mt-1 pt-0"
                v-if="review.JobHopping.actions?.length"
              >
                Actions
              </v-col>
              <v-col cols="12" class="text-body-1">
                <ul>
                  <li v-for="action in review.JobHopping.actions" :key="action">
                    {{ action }}
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="mb-6"
          v-if="(!isProUser && basicTierReview()) || !basicTierReview()"
        >
          <v-card-title class="text-h5">
            Certifications & Education
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="numberFromString('2')"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
            <v-icon color="grey" class="ml-2" v-if="basicTierReview()">
              mdi-lock-outline
            </v-icon>
          </v-card-title>
          <v-card-subtitle class="text-caption">
            Assesses degrees, bootcamps, or certifications (e.g., AWS, Scrum,
            CompTIA) that support your expertise. We score each certification
            based on its relevance and recognition in the industry.
          </v-card-subtitle>
          <v-card-text>
            <v-row v-if="basicTierReview()">
              <v-col cols="12">
                Check available in the Full AI tier
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock with PRO
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="!basicTierReview()">
              <v-col cols="12" class="d-flex justify-space-between">
                <div class="text-body-1">
                  {{ review.CertificatesAndEducation.note }}
                </div>
                <div>
                  <p class="green--text text--darken-1 text-h5 ml-2">
                    <strong class="green--text text--darken-1 text-h3">{{
                      review.CertificatesAndEducation.score
                    }}</strong
                    >/10
                  </p>
                </div>
              </v-col>
              <v-col
                cols="12"
                class="subtitle-2 mb-0 pb-0 mt-1 pt-0"
                v-if="review.CertificatesAndEducation.actions?.length"
              >
                Actions
              </v-col>
              <v-col cols="12" class="text-body-1">
                <ul>
                  <li
                    v-for="action in review.CertificatesAndEducation.actions"
                    :key="action"
                  >
                    {{ action }}
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          class="mb-6"
          v-if="(!isProUser && basicTierReview()) || !basicTierReview()"
        >
          <v-card-title class="text-h5">
            Project Descriptions / Impact Statements
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="numberFromString('2')"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
            <v-icon color="grey" class="ml-2" v-if="basicTierReview()">
              mdi-lock-outline
            </v-icon>
          </v-card-title>
          <v-card-subtitle class="text-caption">
            It's important to quantify your contributions and results in your
            project descriptions. Use metrics, percentages, and specific
            outcomes to demonstrate the impact of your work.
          </v-card-subtitle>
          <v-card-text>
            <v-row v-if="basicTierReview()">
              <v-col cols="12">
                Check available in the Full AI tier
                <v-btn class="mt-3" color="primary" block to="/gopro">
                  Unlock with PRO
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="!basicTierReview()">
              <v-col cols="12" class="d-flex justify-space-between">
                <div class="text-body-1">
                  {{ review.ProjectsAndImpact.note }}
                </div>
                <div>
                  <p class="green--text text--darken-1 text-h5 ml-2">
                    <strong class="green--text text--darken-1 text-h3">{{
                      review.ProjectsAndImpact.score
                    }}</strong
                    >/10
                  </p>
                </div>
              </v-col>
              <v-col
                cols="12"
                class="subtitle-2 mb-0 pb-0 mt-1 pt-0"
                v-if="review.ProjectsAndImpact.actions?.length"
              >
                Actions
              </v-col>
              <v-col cols="12" class="text-body-1">
                <ul>
                  <li
                    v-for="action in review.ProjectsAndImpact.actions"
                    :key="action"
                  >
                    {{ action }}
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- targeted review stuff -->
      <v-col
        cols="12"
        md="10"
        lg="8"
        class="mt-0 mb-0 pt-0 pb-0"
        v-if="targetedTierReview()"
      >
        <v-card class="mb-6">
          <v-card-title class="text-h5"> Job posting </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" class="mt-2 pt-0 mb-1 pb-1">
                <v-icon class="mr-1"> mdi-domain </v-icon>
                {{ review.CompanyName }}
              </v-col>
              <v-col cols="12" class="mt-0 pt-0 mb-1 pb-1">
                <strong>
                  {{ review.JobTitle }}
                </strong>
              </v-col>
              <v-col cols="12" class="text-body-1 mt-0 pt-0 mb-1 pb-1">
                {{ review.JobPostingSummary }}
              </v-col>
              <v-col cols="12">
                <a :href="review.JobPostingUrl" target="_blank">{{
                  review.JobPostingUrl
                }}</a>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6">
          <v-card-title class="text-h5">
            <div>{{ review.MinQualReview.name }}</div>
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="review.MinQualReview.impact"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
          </v-card-title>
          <v-card-subtitle class="text-caption">
            {{ review.MinQualReview.description }}
          </v-card-subtitle>
          <v-card-text>
            <v-row>
              <v-col
                cols="12"
                v-for="dataCheck in review.MinQualChecks"
                :key="dataCheck.name"
              >
                <div class="d-flex justify-space-between text-body-1">
                  {{ dataCheck.name }}
                  <v-icon :color="dataCheck.value ? 'green' : 'red'">
                    {{ dataCheck.value ? "mdi-check-bold" : "mdi-close-thick" }}
                  </v-icon>
                </div>
              </v-col>
              <v-col cols="12" class="d-flex justify-space-between">
                <div class="text-body-1">
                  {{ review.MinQualReview.note }}
                </div>
                <div>
                  <p class="green--text text--darken-1 text-h5 ml-2">
                    <strong class="green--text text--darken-1 text-h3">{{
                      review.MinQualReview.score
                    }}</strong
                    >/10
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mb-6">
          <v-card-title class="text-h5">
            <div>{{ review.PrefQualReview.name }}</div>
            <div class="ml-2">
              <v-rating
                length="3"
                dense
                size="18"
                readonly
                :value="review.PrefQualReview.impact"
                empty-icon="mdi-lightning-bolt-outline"
                full-icon="mdi-lightning-bolt"
                background-color="grey"
                color="red darken-2"
              >
              </v-rating>
            </div>
          </v-card-title>
          <v-card-subtitle class="text-caption">
            {{ review.PrefQualReview.description }}
          </v-card-subtitle>
          <v-card-text>
            <v-row>
              <v-col
                cols="12"
                v-for="dataCheck in review.PrefQualChecks"
                :key="dataCheck.name"
              >
                <div class="d-flex justify-space-between text-body-1">
                  {{ dataCheck.name }}
                  <v-icon :color="dataCheck.value ? 'green' : 'red'">
                    {{ dataCheck.value ? "mdi-check-bold" : "mdi-close-thick" }}
                  </v-icon>
                </div>
              </v-col>
              <v-col cols="12" class="d-flex justify-space-between">
                <div class="text-body-1">
                  {{ review.PrefQualReview.note }}
                </div>
                <div>
                  <p class="green--text text--darken-1 text-h5 ml-2">
                    <strong class="green--text text--darken-1 text-h3">{{
                      review.PrefQualReview.score
                    }}</strong
                    >/10
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import TextSwitchingLoader from "@/components/TextSwitchingLoader.vue";
import ExperienceLevel from "@/components/ExperienceLevel.vue";
import TechnologyIcon from "@/components/TechnologyIcon.vue";
export default {
  components: {
    TextSwitchingLoader,
    ExperienceLevel,
    TechnologyIcon,
  },
  name: "ResumeReview",
  data() {
    return {
      number2: 2,
      number3: 3,
      status: "",
      review: null,
      isProUser: false,
      loadingWithText: false,
      loading: true,
      overallScoreTxt: 0,
      overallScoreCircle: 0,
      errorMsg: "",
      reviewId: "",
      careerProgressionTicksLabels: [
        "Intern", // 0
        "", // 1
        "", // 2
        "Junior", // 3
        "", // 4
        "", // 5
        "Mid-level", // 6
        "", // 7
        "", // 8
        "Senior", // 9
        "", // 10
        "", // 11
        "Lead", // 12
      ],
      takingLongerThanExpected: false,
      // Counters and limits for retrying the review status check
      getReviewStatusCtr: 0,
      getReviewStatusCtrLimit: 6, // Limit to 6 attempts
      getReviewStatusInterval: 5000, // 5 seconds
      // Technology categories:
      // - Programming languages
      // - AI/ML tools
      // - Cloud and infrastructure
      // - Frameworks and Web Tech
      // - Collaboration and data tools
      technologies: [
        {
          group: "Programming languages",
          items: [
            {
              id: "c",
              name: "C",
              level: 2,
              description:
                'You have 3 years of expertise with C working on project "foo" and "bar". Now some more text here to check how it looks. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.',
            },
            {
              id: "python",
              name: "Python",
              level: 3,
              description:
                'You have 5 years of expertise with Python working on project "foo", "bar", and "baz".',
            },
            {
              id: "go",
              name: "Go",
              level: 1,
              description:
                'You have 1 year of expertise with Go working on project "foo".',
            },
          ],
        },
        {
          group: "AI/ML tools",
          items: [
            {
              id: "tensorflow",
              name: "TensorFlow",
              level: 2,
              description:
                'You have 3 years of expertise with TensorFlow working on project "foo" and "bar".',
            },
            {
              id: "pytorch",
              name: "PyTorch",
              level: 1,
              description:
                'You have 1 year of expertise with PyTorch working on project "foo".',
            },
          ],
        },
        {
          group: "Cloud and infrastructure",
          items: [
            {
              id: "aws",
              name: "AWS",
              level: 3,
              description:
                'You have 5 years of expertise with AWS working on project "foo", "bar", and "baz".',
            },
            {
              id: "docker",
              name: "Docker",
              level: 2,
              description:
                'You have 3 years of expertise with Docker working on project "foo" and "bar".',
            },
          ],
        },
      ],
    };
  },
  computed: {
    isMobile() {
      // Return true if the screen is "xs" or "sm"
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
  },
  async mounted() {
    this.$utils.visit(this);
    this.reviewId = this.$route.query.reviewId;
    if (this.$route.query.txtLoading === "true") {
      const query = { ...this.$route.query };
      this.loadingWithText = true;
      this.loading = false;
      delete query.txtLoading;
      this.$router.replace({ query });
    }
    // todo: uncomment when done testing
    // this.isProUser = this.$store.getters.isProUser;
    console.log("isProUser: " + this.isProUser);
    await this.getReviewStatus();
  },
  methods: {
    // overallScoreColor(score) {
    //   if (score >= 90) return "green darken-2";
    //   if (score >= 80) return "green";
    //   if (score >= 70) return "light-green text--darken-1";
    //   if (score >= 60) return "yellow darken-2";
    //   if (score >= 50) return "amber darken-2";
    //   if (score >= 40) return "orange darken-2";
    //   if (score >= 30) return "deep-orange darken-2";
    //   return "red darken-2";
    // },
    impactColor(score) {
      if (score == 3) return "red darken-2";
      if (score == 2) return "orange darken-2";
      return "yellow darken-2";
    },
    scoreColorText(score) {
      if (score >= 9) return "green--text text--darken-2";
      if (score >= 8) return "green--text";
      if (score >= 7) return "light-green--text text--darken-1";
      if (score >= 6) return "yellow--text text--darken-2";
      if (score >= 5) return "amber--text text--darken-2";
      if (score >= 4) return "orange--text text--darken-2";
      if (score >= 3) return "deep-orange--text text--darken-2";
      return "red--text text--darken-2";
    },
    async getReviewStatus() {
      // try to get the status once, if it returns "created" then show the loading text and setup the periodic check.
      // if it returns "done" then show the review.
      // if it returns "error" then show the error message.
      try {
        this.getReviewStatusCtr++;
        if (this.getReviewStatusCtr > 3) {
          this.takingLongerThanExpected = true;
        }
        if (this.getReviewStatusCtr > this.getReviewStatusCtrLimit) {
          this.errorMsg = "Something went wrong, please try again in a moment.";
          this.loading = false;
          return;
        }
        console.log("getReviewStatus called with reviewId: " + this.reviewId);
        if (!this.reviewId) {
          console.log("No reviewId provided");
          throw new Error("Looks like this link is invalid (no reviewId)");
        }
        if (this.reviewId.endsWith("-example")) {
          var resp = await this.$utils.callBackendPost(
            this,
            "/pub/getReviewStatusExample",
            { reviewId: this.reviewId },
          );
        } else {
          var resp = await this.$utils.callBackendPost(
            this,
            "/api/getReviewStatusAI",
            { reviewId: this.reviewId },
          );
        }

        console.log("resp: " + JSON.stringify(resp, null, 2));
        this.status = resp.status;
        console.log(
          "resp.review.InvalidJobPosting: " + resp.review.InvalidJobPosting,
        );
        if (resp.review.NotResume) {
          throw new Error(
            'The document "' +
              resp.review.ResumeFileName +
              '" does not seem to be a resume. Please upload a valid resume.',
          );
        }
        if (resp.review.InvalidJobPosting) {
          throw new Error(
            "The job posting URL you provided does not seem to be valid: " +
              resp.review.JobPostingUrl +
              ". Please check the URL and try again.",
          );
        }
        this.review = resp.review;
        // console.log("status: " + this.status);
        // console.log("review: " + JSON.stringify(this.review, null, 2));
        this.updateUI();
      } catch (error) {
        console.log("Error getting review status: ", error);
        this.loading = false;
        this.loadingWithText = false;
        this.errorMsg = error;
      }
    },
    updateUI() {
      if (this.status === "created") {
        this.loadingWithText = true;
        this.loading = false;
        // check the status again in 5 seconds
        setTimeout(() => {
          this.getReviewStatus();
        }, this.getReviewStatusInterval);
      } else if (this.status === "done") {
        this.loadingWithText = false;
        this.loading = false;
        // increase overallScore over the next 2 seconds
        this.animateOverallScore();
      } else if (this.status === "error") {
        this.errorMsg =
          "Sorry, an error occurred while processing your review. We pinged our dev to fix it, please try again later.";
        this.loadingWithText = false;
        this.loading = false;
      }
      console.log("status: " + this.status);
      console.log("loadingWithText: " + this.loadingWithText);
      console.log("loading: " + this.loading);
    },
    animateOverallScore() {
      // Simulate an animation for the overall score
      const targetScore = this.review.OverallScore;
      let currentScore = 0;
      const increment = Math.ceil(targetScore / 20); // Increment by 5% of the target score
      const interval = setInterval(() => {
        if (currentScore < targetScore) {
          currentScore += increment;
          if (currentScore > targetScore) {
            currentScore = targetScore;
          }
          this.overallScoreTxt = currentScore;
          if (currentScore == targetScore) {
            this.overallScoreCircle = currentScore;
          }
        } else {
          clearInterval(interval);
        }
      }, 30); // Update every 30ms
    },
    numberFromString(str) {
      return Number(str);
    },
    basicTierReview() {
      return this.review && this.review.Tier === 101;
    },
    targetedTierReview() {
      return this.review && this.review.Tier === 110;
    },
  },
};
</script>

<style scoped>
.main-card {
  width: 100vw;
  max-width: 1200px;
}
.fade-out {
  position: relative;
}
.fade-out:after {
  content: "";
  height: 80%;
  width: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  background: linear-gradient(to bottom, transparent, white);
  pointer-events: none;
}
</style>
